import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  TextField,
  Typography,
  Stack,
  Chip,
} from '@mui/material';

const PRESETS: { label: string; cron: string; description: string }[] = [
  { label: 'Every hour', cron: '0 * * * *', description: 'At the start of every hour' },
  { label: 'Every 6 hours', cron: '0 */6 * * *', description: 'Every 6 hours' },
  { label: 'Daily', cron: '0 0 * * *', description: 'Daily at midnight UTC' },
  { label: 'Weekly', cron: '0 0 * * 1', description: 'Weekly on Monday at midnight UTC' },
];

interface CronExpressionInputProps {
  value: string;
  onChange: (cron: string) => void;
  error?: string;
}

function describeCron(expression: string): string {
  const DESCRIPTIONS: Record<string, string> = {
    '* * * * *': 'Every minute',
    '*/5 * * * *': 'Every 5 minutes',
    '*/10 * * * *': 'Every 10 minutes',
    '*/15 * * * *': 'Every 15 minutes',
    '*/30 * * * *': 'Every 30 minutes',
    '0 * * * *': 'Every hour',
    '0 */2 * * *': 'Every 2 hours',
    '0 */3 * * *': 'Every 3 hours',
    '0 */4 * * *': 'Every 4 hours',
    '0 */6 * * *': 'Every 6 hours',
    '0 */8 * * *': 'Every 8 hours',
    '0 */12 * * *': 'Every 12 hours',
    '0 0 * * *': 'Daily at midnight',
    '0 6 * * *': 'Daily at 6:00 AM',
    '0 12 * * *': 'Daily at noon',
    '0 18 * * *': 'Daily at 6:00 PM',
    '0 0 * * 1': 'Weekly on Monday',
    '0 0 * * 0': 'Weekly on Sunday',
    '0 0 1 * *': 'Monthly on the 1st',
    '0 0 15 * *': 'Monthly on the 15th',
  };
  const normalized = expression.trim().replace(/\s+/g, ' ');
  return DESCRIPTIONS[normalized] || `Custom: ${normalized}`;
}

export default function CronExpressionInput({
  value,
  onChange,
  error,
}: CronExpressionInputProps) {
  const [showCustom, setShowCustom] = useState(
    () => !PRESETS.some((p) => p.cron === value) && value !== '',
  );

  const description = useMemo(() => (value ? describeCron(value) : ''), [value]);
  const activePreset = PRESETS.find((p) => p.cron === value);

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" color="text.secondary">
        Schedule frequency
      </Typography>

      <ButtonGroup variant="outlined" size="small">
        {PRESETS.map((preset) => (
          <Button
            key={preset.cron}
            variant={activePreset?.cron === preset.cron ? 'contained' : 'outlined'}
            onClick={() => {
              onChange(preset.cron);
              setShowCustom(false);
            }}
          >
            {preset.label}
          </Button>
        ))}
        <Button
          variant={showCustom ? 'contained' : 'outlined'}
          onClick={() => setShowCustom(true)}
        >
          Custom
        </Button>
      </ButtonGroup>

      {showCustom && (
        <TextField
          label="Cron expression"
          placeholder="*/30 * * * *"
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error || 'Standard 5-field cron: minute hour day month weekday'}
          fullWidth
        />
      )}

      {value && (
        <Box>
          <Chip label={description} size="small" color="info" variant="outlined" />
        </Box>
      )}
    </Stack>
  );
}
